import { Divider, Typography, useTheme } from "@mui/material";
import type { FC } from "react";
import { ActivityItem } from "web_myplann/Activities/Components/ActivityItem";
import { LoadNextRow } from "web_myplann/Activities/Components/LoadNextRow";
import { TaskItem } from "web_myplann/Activities/Components/TaskItem";
import { ACTIVITY_LIST_COLLAPSED_SIZE } from "web_myplann/Activities/constants";
import {
	type ActivityOrTaskListItem,
	isActivityListItem,
} from "web_myplann/Activities/Helpers/isActivityListItem";
import { useAssignedAgentsQuery } from "web_myplann/Api/Advisor/useAssignedAgentsQuery";
import { UserAgentStatusEnum } from "web_myplann/Infrastructure/Api/Api";
import { ToggleAll } from "web_myplann/Portfolio/Components/ToggleAll";
import { useHistoryEntryToggle } from "web_myplann/Shared/Hooks/useHistoryEntryToggle";

type Props = {
	label: string;
	items: ActivityOrTaskListItem[];
	totalCount?: number;
	hasNextPage?: boolean;
	isFetchingNextPage?: boolean;
	fetchNextPage?: () => void;
	defaultNormalSize?: number;
	emptyGroupText?: string;
	noDivider?: boolean;
};

export const ActivityListGroup: FC<Props> = ({
	label,
	items,
	totalCount,
	hasNextPage,
	isFetchingNextPage,
	fetchNextPage,
	defaultNormalSize,
	emptyGroupText,
	noDivider,
}) => {
	const theme = useTheme();
	const { data: assignedAgents } = useAssignedAgentsQuery(
		UserAgentStatusEnum.Connected,
	);
	const hasMoreConnectedAgents = (assignedAgents?.data.length ?? 0) > 1;

	const count = totalCount ?? items.length;
	const collapsedSize = defaultNormalSize ?? ACTIVITY_LIST_COLLAPSED_SIZE;
	const [expanded, toggleExpanded] = useHistoryEntryToggle(label);

	const hideSection = count === 0 && !emptyGroupText;
	const diplayEmptyText = count === 0 && emptyGroupText;

	if (hideSection) {
		return null;
	}

	return (
		<>
			<Typography
				variant="deprecatedH3"
				color={theme.palette.deprecated.grey[700]}
			>
				{label}
			</Typography>

			{diplayEmptyText && (
				<Typography
					variant="deprecatedBody1"
					color={theme.palette.deprecated.grey[700]}
				>
					{emptyGroupText}
				</Typography>
			)}

			{(expanded ? items : items.slice(0, collapsedSize)).map((item) =>
				isActivityListItem(item) ? (
					<ActivityItem
						key={item.id}
						data={item}
						hasMoreConnectedAgents={hasMoreConnectedAgents}
					/>
				) : (
					<TaskItem
						key={item.id}
						data={item}
						hasMoreConnectedAgents={hasMoreConnectedAgents}
					/>
				),
			)}

			{expanded && hasNextPage && fetchNextPage && (
				<LoadNextRow loading={!!isFetchingNextPage} onClick={fetchNextPage} />
			)}

			{count > collapsedSize && (
				<ToggleAll all={expanded} onClick={toggleExpanded} />
			)}

			{!noDivider && (
				<Divider
					sx={{
						borderWidth: 1,
						borderColor: theme.palette.deprecated.grey[100],
					}}
				/>
			)}
		</>
	);
};
